import { useState, useEffect } from "react";
import TransparencyScore from "./TransparencyScore/TransparencyScore";
import AttributePage from "./Attribute";
import Packaging from "./Packaging";
import AnimalWelfare from "./AnimalWelfare";
import ClimateChange from "./ClimateChange";
import RiskStatusThreshold from "./RiskStatusThreshold/Table";
import BreadCrumbs from "./BreadCrumbs";
import { COLUMNTITLES } from "@/utils/Enum";
import AdminTabs from "../../interfaces/AdminTabs";
import { usePageTracking } from "@/hooks/usePageTracking";

const ADMIN_TAB_STORAGE_KEY = "scrm-admin-selected-tab";

const styles = {
  tabButton: (isActive: boolean, isDisabled: boolean) =>
    `bg-none border-none mt-[8px] inline-flex px-4 py-2 rounded-t-lg whitespace-nowrap transition-colors duration-200 ${isActive ? "bg-white cursor-pointer" : "bg-gray-400"
    } ${isDisabled && "bg-gray-400/25 text-opacity-50 cursor-default"}
    h-[40px] text-[14px] font-medium leading-normal`,
  archiveButton:
    "float-right mr-[20px] mb-[16px] px-[16px] py-[6px] rounded-sm border-2 border-blue-600 text-blue-600 whitespace-nowrap text-[14px] font-medium leading-normal",
};

const adminTabs: AdminTabs[] = [
  {
    label: COLUMNTITLES.OVERALL,
    content: <AttributePage category="overall" />,
  },
  {
    label: COLUMNTITLES.TRANSPARENCY,
    content: <TransparencyScore />,
  },
  {
    label: COLUMNTITLES.ENVIRONMENT,
    content: <AttributePage category="environmental" />,
  },
  {
    label: COLUMNTITLES.HUMANRIGHTS,
    content: <AttributePage category="humanrights" />,
  },
  {
    label: COLUMNTITLES.RISK_STATUS_THRESHOLD,
    content: <RiskStatusThreshold />,
    disabled: false,
  },
  {
    label: COLUMNTITLES.PACKAGING,
    content: <Packaging />,
    disabled: true,
  },
  {
    label: COLUMNTITLES.ANIMALWELFARE,
    content: <AnimalWelfare />,
    disabled: true,
  },
  {
    label: COLUMNTITLES.CLIMATECHANGE,
    content: <ClimateChange />,
    disabled: true,
  },
];

/**
 * Get the previously selected tab from localStorage
 */
const getSavedTabIndex = (): number => {
  try {
    const saved = localStorage.getItem(ADMIN_TAB_STORAGE_KEY);
    if (saved !== null) {
      const index = parseInt(saved, 10);
      // Validate the index is within bounds and not disabled
      if (
        index >= 0 &&
        index < adminTabs.length &&
        !adminTabs[index].disabled
      ) {
        return index;
      }
    }
  } catch (error) {
    console.error("Failed to load saved tab index:", error);
  }
  return 0; // Default to first tab
};

/**
 * Save the selected tab to localStorage
 */
const saveTabIndex = (index: number): void => {
  try {
    localStorage.setItem(ADMIN_TAB_STORAGE_KEY, index.toString());
  } catch (error) {
    console.error("Failed to save tab index:", error);
  }
};

/**
 * Get friendly tab name for telemetry
 */
const getTabDisplayName = (index: number): string => {
  const label = adminTabs[index]?.label as COLUMNTITLES;
  if (!label) return "Unknown";

  const labelDisplayMap: Partial<Record<COLUMNTITLES, string>> = {
    [COLUMNTITLES.OVERALL]: "Overall",
    [COLUMNTITLES.TRANSPARENCY]: "Transparency",
    [COLUMNTITLES.ENVIRONMENT]: "Environmental",
    [COLUMNTITLES.HUMANRIGHTS]: "Human Rights",
    [COLUMNTITLES.RISK_STATUS_THRESHOLD]: "Threshold",
  };

  return labelDisplayMap[label] || label;
};

const AdminDefaultLayout = ({ className }: { className?: string }) => {
  const [activeTab, setActiveTab] = useState(getSavedTabIndex());

  // Track initial admin page view once per session
  usePageTracking("Admin Dashboard", {
    initialTab: getTabDisplayName(getSavedTabIndex()),
  });

  const handleTabClick = (index: number, disabled?: boolean) => {
    // Check if the tab is disabled OR if it's already the active tab
    if (disabled || index === activeTab) {
      return; // Exit early: No state change, no log
    }

    // Update state and persist
    setActiveTab(index);
    saveTabIndex(index);

    // Note: We don't log tab changes as telemetry events
    // Only the initial admin page view is tracked once per session
    // TODO: If you want to track tab changes in the future, use useEventTracking
  };

  // Load saved tab on mount
  useEffect(() => {
    const savedIndex = getSavedTabIndex();
    setActiveTab(savedIndex);
  }, []);

  return (
    <div className={`flex flex-col min-h-screen ${className}`}>
      <div className="bg-gray-200">
        <div className="py-4 pb-10">
          <BreadCrumbs />
        </div>

        <div className="flex justify-between">
          <div className="flex mt-1 ml-10">
            {adminTabs.map((tab, index) => (
              <button
                key={index}
                id={tab.label}
                className={styles.tabButton(
                  activeTab === index,
                  !!tab.disabled,
                )}
                onClick={() => handleTabClick(index, tab.disabled)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Uncomment to enable the archive tab */}
          {/* <button
            id={archiveTab.label}
            className={styles.archiveButton}
            onClick={handleArchiveClick}
          >
            {archiveTab.label}
          </button> */}
        </div>
      </div>

      <div className="bg-white grow-5" id={adminTabs[activeTab].label}>
        {adminTabs[activeTab].content}
      </div>
    </div>
  );
};

export default AdminDefaultLayout;
